import Panel from './Panel.jsx';
import { formatCurrency, formatHumanDate } from '@/lib/format.js';
import { parseBrData } from '@/lib/br-data.js';

// BrProjectPanel (BR-REPEAT-BIND Ship 4 item 3) — the Property line's
// entity panel, taking the slot the aviation panels occupy on other lines.
// Read-only until Ship 2's BrDataSheet editor lands: jobsite, project
// description, values and dates, coverage limits table, premium + TRIA,
// all read from the parsed Builders_Risk_Data blob. A property record
// renders zero aviation vocabulary (the structural gate scans this file).

function KV({ k, v }) {
  if (v == null || v === '') return null;
  return (
    <div className="flex justify-between gap-3 py-[3px] text-[12px]">
      <span className="text-text-dim">{k}</span>
      <span className="text-right text-text">{v}</span>
    </div>
  );
}

function money(n) {
  return n == null ? null : formatCurrency(n);
}

// One coverage row: label · limit · deductible. Rendered only when the
// limit or the deductible is present so partial blobs stay quiet.
function CovRow({ label, limit, deductible }) {
  if (limit == null && deductible == null) return null;
  return (
    <tr className="border-t border-border/60">
      <td className="py-1.5 pr-3 text-text-muted">{label}</td>
      <td className="py-1.5 pr-3 text-right font-mono text-[11.5px] text-text">{money(limit) || '·'}</td>
      <td className="py-1.5 text-right font-mono text-[11.5px] text-text-muted">{money(deductible) || '·'}</td>
    </tr>
  );
}

export default function BrProjectPanel({ deal }) {
  const data = parseBrData(deal?.Builders_Risk_Data);
  const project = data?.project || {};
  const coverage = data?.coverage || {};
  const applicant = data?.applicant || {};
  const addr = project.jobsite_address || {};
  const jobsiteLine = [addr.street, [addr.city, addr.state].filter(Boolean).join(', '), addr.zip]
    .filter(Boolean)
    .join(' · ');

  if (!data) {
    return (
      <Panel title="Project & Coverage" empty="No builders risk data on this deal yet. The BrDataSheet editor (Ship 2) is where it gets entered." />
    );
  }

  const premium = coverage.annual_premium;
  const tria = coverage.tripra_premium;

  return (
    <div className="space-y-4">
      <Panel title="Project">
        {project.description ? (
          <p className="mb-2.5 text-[12.5px] leading-snug text-text">{project.description}</p>
        ) : null}
        <KV k="Jobsite" v={jobsiteLine || null} />
        <KV k="Occupancy" v={project.occupancy} />
        <KV k="Project type" v={project.single_or_multi} />
        <KV k="Completed value" v={money(project.completed_value)} />
        <KV k="Commencement" v={formatHumanDate(project.commencement_date)} />
        <KV k="Est. completion" v={formatHumanDate(project.est_completion_date)} />
        <KV k="Applicant role" v={applicant.role_on_project} />
        <KV k="Entity type" v={applicant.entity_type} />
      </Panel>

      <Panel title="Coverage & Limits">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-dim">
              <th className="pb-1.5 pr-3 text-left font-semibold">Coverage</th>
              <th className="pb-1.5 pr-3 text-right font-semibold">Limit</th>
              <th className="pb-1.5 text-right font-semibold">Deductible</th>
            </tr>
          </thead>
          <tbody>
            <CovRow label="Any one jobsite" limit={coverage.any_jobsite_limit} deductible={coverage.any_jobsite_deductible} />
            <CovRow label="Transit" limit={coverage.transit_limit} deductible={coverage.transit_deductible} />
            <CovRow label="Temporary storage" limit={coverage.temp_storage_limit} deductible={coverage.temp_storage_deductible} />
            <CovRow label="Any one loss" limit={coverage.any_one_loss_limit} deductible={coverage.any_one_loss_deductible} />
            <CovRow label="Earthquake" limit={coverage.earthquake_limit} />
            <CovRow label="Flood" limit={coverage.flood_limit} />
          </tbody>
        </table>
        <div className="mt-2.5 border-t border-border pt-2">
          {/* A percentage deductible ("2%") is a string; formatCurrency returns
              a dash for it, which is truthy, so the old `|| String(...)`
              fallback never ran and the row printed a dash over a real value. */}
          <KV k="Windstorm deductible" v={coverage.windstorm_deductible == null ? null : (typeof coverage.windstorm_deductible === 'number' ? money(coverage.windstorm_deductible) : String(coverage.windstorm_deductible))} />
          <KV k="Coinsurance" v={coverage.coinsurance} />
          <KV k="Permission to occupy" v={coverage.permission_to_occupy} />
          <KV k="TRIA" v={coverage.tripra_accepted == null ? null : coverage.tripra_accepted ? 'Accepted' : 'Rejected'} />
        </div>
      </Panel>

      <Panel title="Premium">
        <KV k="Annual premium" v={money(premium)} />
        <KV k="TRIA premium" v={money(tria)} />
        <KV
          k="Total"
          v={premium != null || tria != null ? money((premium || 0) + (tria || 0)) : null}
        />
        {premium == null ? (
          <p className="text-xs italic text-text-dim">No premium entered yet.</p>
        ) : null}
      </Panel>
    </div>
  );
}
